import {
  MenuContent,
  MenuRadioItem,
  MenuRadioItemGroup,
  MenuRoot,
  MenuTrigger,
} from '@/components/ui/menu';
import { PaymentNeededBadge } from '@/components/integrations/PaymentNeededBadge';
import { bareAdAccountId } from '@/libs/meta-ads-links';
import { Box, Button, HStack, Image, Text, VStack } from '@chakra-ui/react';
import type { MentionBoostAdAccountDto } from '@fluenceur/types';
import { NavArrowDown } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface AdAccountFieldProps {
  // Every account the brand can pick, payable or not. Only accounts Meta
  // disabled are absent: those cannot be chosen at all.
  adAccounts: MentionBoostAdAccountDto[];
  selectedId: string | null;
  onChange: (id: string) => void;
}

/**
 * The account id and currency, shown under the name. A brand can own several
 * accounts sharing a name, so the name alone lets the marketer neither pick the
 * right one nor quote it to support.
 */
function AccountMeta({ account }: { account: MentionBoostAdAccountDto }) {
  return (
    <Text fontSize="xs" color="gray.500" lineClamp={1}>
      {account.currencyCode
        ? `${bareAdAccountId(account.id)} · ${account.currencyCode}`
        : bareAdAccountId(account.id)}
    </Text>
  );
}

/**
 * Ad-account chooser shown as the first setup field when the brand owns several
 * selectable accounts. It surfaces the single account that will be billed, not
 * the whole list; "Change" opens a compact menu of the others.
 *
 * Nothing is preselected when the server resolved no account: the empty slot is
 * the honest reading of "the brand has not decided which account pays", and the
 * launch stays unavailable until it does.
 */
export function AdAccountField({ adAccounts, selectedId, onChange }: AdAccountFieldProps) {
  const { t } = useTranslation();
  const selected = adAccounts.find((account) => account.id === selectedId) ?? null;

  return (
    <HStack
      justify="space-between"
      gap={3}
      border="1px solid"
      borderColor="gray.200"
      rounded="lg"
      px={3}
      py={2.5}
      bg="white"
    >
      <HStack gap={3} minW={0}>
        <Box bg="gray.100" rounded="md" p={2} display="flex" flexShrink={0}>
          <Image src="/images/integrations/meta.svg" boxSize="18px" alt="" />
        </Box>
        <VStack align="start" gap={0} minW={0}>
          <HStack gap={2} minW={0}>
            <Text fontSize="sm" fontWeight="medium" color="gray.900" lineClamp={1}>
              {selected?.name ?? t('mentions.boost.setup.adAccount.placeholder')}
            </Text>
            {selected && !selected.paymentReady && <PaymentNeededBadge />}
          </HStack>
          {selected && <AccountMeta account={selected} />}
        </VStack>
      </HStack>

      <MenuRoot positioning={{ placement: 'bottom-end' }}>
        <MenuTrigger asChild>
          <Button size="xs" variant="ghost" colorPalette="gray" flexShrink={0} gap={1}>
            {t(
              selected
                ? 'mentions.boost.setup.adAccount.change'
                : 'mentions.boost.setup.adAccount.choose'
            )}
            <NavArrowDown width={14} height={14} />
          </Button>
        </MenuTrigger>
        {/* Render inline (no body portal): this menu lives inside the boost
            preview Dialog. A body-portaled content sits outside the dialog's
            focus trap, which swallows the item click (menu closes, onValueChange
            never fires) and kills keyboard nav. Mirrors TemplatePicker's
            "select within dialog" fix. */}
        <MenuContent minW="260px" portalled={false}>
          <MenuRadioItemGroup
            value={selectedId ?? undefined}
            onValueChange={(details) => onChange(details.value)}
          >
            {adAccounts.map((account) => (
              <MenuRadioItem key={account.id} value={account.id}>
                <VStack align="start" gap={0} minW={0}>
                  <HStack gap={2} minW={0}>
                    <Text fontSize="sm" color="gray.900" lineClamp={1}>
                      {account.name}
                    </Text>
                    {!account.paymentReady && <PaymentNeededBadge />}
                  </HStack>
                  <AccountMeta account={account} />
                </VStack>
              </MenuRadioItem>
            ))}
          </MenuRadioItemGroup>
        </MenuContent>
      </MenuRoot>
    </HStack>
  );
}
